import { GrowthBook } from '@growthbook/growthbook';
import DataLayerHelper from '../helpers/tagManager/DataLayerHelper';

let growthBookInstance = null;
let growthBookInitPromise = null;

function pushExperimentEvent(experiment, result) {
  DataLayerHelper.pushEvent('af_experiment_experience', {
    experiment_id: experiment.key,
    variant_id: result.key,
    variant_value: result.value,
  });
}

function getGaClientIdFromCookie() {
  try {
    const cookies = document.cookie.split(';');

    for (const rawCookie of cookies) {
      const cookie = rawCookie.trim();

      if (!cookie.startsWith('_ga=')) {
        continue;
      }

      const gaValue = cookie.split('=')[1];

      if (!gaValue) {
        return null;
      }

      const parts = gaValue.trim().split('.');

      if (parts.length >= 4) {
        return `${parts.at(-2)}.${parts.at(-1)}`;
      }
    }
  } catch (error) {
    console.error('[growthbook] Error reading GA client ID:', error);
  }

  return null;
}

function waitForGaClientId(timeoutMs) {
  const maxAttempts = 10;
  const pollInterval = timeoutMs / maxAttempts;
  let attempts = 0;

  return new Promise((resolve) => {
    function poll() {
      const clientId = getGaClientIdFromCookie();

      if (clientId) {
        resolve(clientId);
        return;
      }

      attempts += 1;
      if (attempts >= maxAttempts) {
        resolve(null);
        return;
      }

      setTimeout(poll, pollInterval);
    }

    poll();
  });
}

export async function initGrowthBook(config) {
  if (!config?.apiHost || !config?.clientKey) {
    return null;
  }

  if (growthBookInstance) {
    return growthBookInstance;
  }

  if (growthBookInitPromise) {
    return growthBookInitPromise;
  }

  growthBookInitPromise = (async () => {
    try {
      const clientId = await waitForGaClientId(config.gaClientIdTimeoutMs || 2000);

      if (!clientId) {
        return null;
      }

      const gb = new GrowthBook({
        apiHost: config.apiHost,
        clientKey: config.clientKey,
        enableDevMode: process.env.NODE_ENV === 'development',
        trackingCallback: pushExperimentEvent,
      });

      await gb.init({ streaming: true });
      gb.setAttributes({ id: clientId, URL: document.URL });
      growthBookInstance = gb;
      return growthBookInstance;
    } catch (error) {
      console.error('[growthbook] Init error:', error);
      return null;
    } finally {
      growthBookInitPromise = null;
    }
  })();

  return growthBookInitPromise;
}

export async function getFeatureValue(config) {
  if (!config?.featureKey) {
    return config?.fallback ?? null;
  }

  const fallback = config.fallback === undefined ? null : config.fallback;
  const gb = await initGrowthBook(config);

  if (!gb) {
    return fallback;
  }

  try {
    return gb.getFeatureValue(config.featureKey, fallback);
  } catch (error) {
    console.error('[growthbook] getFeatureValue error:', error);
    return fallback;
  }
}

export function __resetGrowthBookForTests() {
  growthBookInstance = null;
  growthBookInitPromise = null;
}
